var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import cn from 'classnames';
import { Children, useCallback, useEffect, useRef, useState } from 'react';
import ScrollContainer from 'react-indiana-drag-scroll';
import { Divider } from '@snack-uikit/divider';
import { extractSupportProps } from '@snack-uikit/utils';
import { MARKER_POSITION, ORIENTATION, TYPE } from '../../constants';
import { TabBarContext, useTabsContext } from '../../context';
import { ScrollButton } from '../ScrollButton';
import { useFocusControl, useScrollContainer } from './hooks';
import styles from './styles.module.css';
const SCROLL_CONTAINER_ORIENTATION_MAP = {
    [ORIENTATION.Horizontal]: styles.horizontalScrollContainer,
    [ORIENTATION.Vertical]: styles.verticalScrollContainer,
};
export function TabBar(_a) {
    var { children, className, type = TYPE.Primary, orientation = ORIENTATION.Horizontal, markerPosition = MARKER_POSITION.After, after } = _a, otherProps = __rest(_a, ["children", "className", "type", "orientation", "markerPosition", "after"]);
    const scrollContainerRef = useRef(null);
    const { selectedTab, setSelectedTab } = useTabsContext();
    const { hasOverflow, scrollLeft, scrollRight, scrollTop, scrollBottom } = useScrollContainer(scrollContainerRef);
    const selectedRef = useRef();
    const [markerScrollPosition, setMarkerScrollPosition] = useState({});
    const [focusedTab, setFocusedTab] = useState();
    useEffect(() => {
        /** Если "сверху" нет указания какой таб выбран, триггерим выбор первого доступного */
        if (!selectedTab) {
            const firsEnabled = Children.map(children, ({ props }) => props).find(({ disabled }) => !disabled);
            firsEnabled && setSelectedTab(firsEnabled.value);
        }
    }, [children, selectedTab, setSelectedTab]);
    const updateMarkPosition = useCallback((element) => {
        if (orientation === 'horizontal') {
            setMarkerScrollPosition({
                left: element.offsetLeft,
                width: element.offsetWidth,
            });
        }
        else {
            setMarkerScrollPosition({
                top: element.offsetTop,
                height: element.offsetHeight,
            });
        }
        selectedRef.current = element;
    }, [orientation]);
    useEffect(() => {
        if (!selectedRef.current) {
            return;
        }
        const updatePosition = () => selectedRef.current && updateMarkPosition(selectedRef.current);
        updatePosition();
        const resizeObserver = new ResizeObserver(updatePosition);
        resizeObserver.observe(selectedRef.current);
        return () => {
            resizeObserver.disconnect();
        };
    }, [children, type, updateMarkPosition]);
    const scrollContainerToElement = useCallback((element) => {
        const container = scrollContainerRef.current;
        if (element && container) {
            if (orientation === 'vertical') {
                const overflowTop = element.offsetTop < container.scrollTop;
                const overflowBottom = element.offsetTop + element.offsetHeight > container.scrollTop + container.offsetHeight;
                if (overflowTop || overflowBottom) {
                    const count = element.offsetTop - container.offsetHeight / 2 + element.offsetHeight / 2;
                    container.scroll({ top: count, behavior: 'smooth' });
                }
                return;
            }
            const overflowLeft = element.offsetLeft < container.scrollLeft;
            const overflowRight = element.offsetLeft + element.offsetWidth > container.scrollLeft + container.offsetWidth;
            if (overflowLeft || overflowRight) {
                const count = element.offsetLeft - container.offsetWidth / 2 + element.offsetWidth / 2;
                container.scroll({ left: count, behavior: 'smooth' });
            }
        }
    }, [orientation]);
    const onSelectHandler = useCallback((element) => {
        updateMarkPosition(element);
        scrollContainerToElement(element);
    }, [scrollContainerToElement, updateMarkPosition]);
    const onFocusHandler = useCallback((element, value) => {
        setFocusedTab(value);
        scrollContainerToElement(element);
    }, [scrollContainerToElement]);
    const [getPrev, getNext] = useFocusControl(children);
    const handleKeyDown = useCallback((e) => {
        let newFocusTab;
        if (e.key === 'ArrowRight')
            newFocusTab = getNext(focusedTab || selectedTab);
        if (e.key === 'ArrowLeft')
            newFocusTab = getPrev(focusedTab || selectedTab);
        if (e.key === 'ArrowUp')
            newFocusTab = getPrev(focusedTab || selectedTab);
        if (e.key === 'ArrowDown')
            newFocusTab = getNext(focusedTab || selectedTab);
        if (newFocusTab) {
            e.preventDefault();
            setFocusedTab(newFocusTab);
        }
    }, [getPrev, focusedTab, selectedTab, getNext, setFocusedTab]);
    return (_jsxs("div", Object.assign({ className: cn(styles.tabBar, className), role: 'tablist', "data-orientation": orientation }, extractSupportProps(otherProps), { children: [!otherProps.disableDivider && (_jsx(Divider, { weight: 'regular', orientation: orientation, "data-position": markerPosition, className: styles.divider })), _jsxs("div", { className: styles.tabBarMain, "data-test-id": 'tabs__bar-wrap', children: [_jsx(ScrollContainer, { className: SCROLL_CONTAINER_ORIENTATION_MAP[orientation], innerRef: scrollContainerRef, children: _jsxs("div", { className: styles.tabsRow, "data-type": type, "data-orientation": orientation, onKeyDown: handleKeyDown, children: [_jsx(TabBarContext.Provider, { value: { onSelect: onSelectHandler, type, orientation, focusedTab, onFocus: onFocusHandler }, children: children }), _jsx("div", { className: styles.marker, style: markerScrollPosition, "data-position": markerPosition, "data-orientation": orientation })] }) }), hasOverflow.left && (_jsx(ScrollButton, { direction: 'left', onClick: scrollLeft, orientation: orientation, type: type })), hasOverflow.right && (_jsx(ScrollButton, { direction: 'right', onClick: scrollRight, orientation: orientation, type: type })), hasOverflow.top && _jsx(ScrollButton, { direction: 'top', onClick: scrollTop, orientation: orientation, type: type }), hasOverflow.bottom && (_jsx(ScrollButton, { direction: 'bottom', onClick: scrollBottom, orientation: orientation, type: type }))] }), after && (_jsx("div", { "data-test-id": 'tabs__bar__after', "data-orientation": orientation, "data-type": type, className: styles.tabBarAfter, children: after }))] })));
}
